/**
* WealthHero - Floating header + white data card
*/
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { formatCurrencyWhole } from "../../utils/formatters";
import { usePlan } from "../../utils/plan"; // plan_09
import PlanPath from "../common/PlanPath"; // plan_11
import { useWealthView, ViewToggle } from "./WealthPaths"; // plan_22

const WealthHero = ({
  theme = 'pink',
  viewMonth,
  onPrevMonth,
  onNextMonth,
  activeView,
  onViewChange,
  totalGoal,
  totalSaved,
  totalProjected,
  totalInvestmentGoal,
  totalInvested,
  totalInvestmentProjected,
  savingsInterest = 0,
  investmentInterest = 0,
  totalExpectedIncome = 0,
  totalPaydays = 0,
  nextPaydayDate = null,
  canUseFutureIncome = false,
  canViewHistory = true,
  showFeaturePaywall,
  onInfoClick,
  chart = null, // plan_09: Money plan puts the chart in the top card
  nextPaydayName = '',
  paySteps = [], // plan_11
  nextPaydayAmount = 0,
  path = null, // plan_22
  banner = null, // dark_13: a nudge under the tabs
}) => {
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  const plan = usePlan(); // plan_09
  const [wView, setWView] = useWealthView(); // plan_22

  // Theme-aware tile palette — sourced from Budget's Income/Spent/Saved-Inv/Leftover tiles
  // (BudgetHero.jsx) so Wealth's tiles stay in the same family per theme.
  const tileColors = {
    green: isPink
      ? { bg: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)', text: '#5C7A4A' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)', text: '#5C7A4A' }
      : { bg: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)', text: '#2F7A80' },
    lavender: isPink
      ? { bg: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)', text: '#7A5FB0' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)', text: '#9A5B3C' }
      : { bg: 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)', text: '#6B5FB0' },
    blue: isPink
      ? { bg: 'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)', text: '#3C6693' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #EDE3D5 0%, #E2D3BC 100%)', text: '#7A6A4A' }
      : { bg: 'linear-gradient(135deg, #E6E6F5 0%, #D6D6ED 100%)', text: '#5B4F93' },
    pink: isPink
      ? { bg: 'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)', text: '#C23A72' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #F7E7D9 0%, #F0D8C2 100%)', text: '#B5651D' }
      : { bg: 'linear-gradient(135deg, #E1E8FA 0%, #D2DEF5 100%)', text: '#3A56C2' },
  };

  const shortMonthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const today = new Date();
  const isSavings = activeView === 'savings';
  const isPlanned = activeView === 'planned';
  const isInvestments = activeView === 'investments';
  const isNetWorth = activeView === 'networth';

  const pillActiveGradient = isPink
    ? 'linear-gradient(135deg, #F5A8C1, #E27BA1)'
    : isWarm
    ? 'linear-gradient(135deg, #B08D5B, #7C5C3A)'
    : 'linear-gradient(135deg, #6366f1, #4361EE)';

  const labelColor = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const accentColor = isPink ? '#e879a0' : isWarm ? '#B07D50' : '#818cf8';
  const goodColor = '#2E9E7B';

  const getDaysUntilNextPayday = () => {
    if (!nextPaydayDate) return '—';
    const next = new Date(nextPaydayDate);
    const diff = Math.ceil((next - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'Past due';
    if (diff === 0) return 'Today!';
    return `${diff}d away`;
  };

  const getHeroTitle = () => {
    if (isNetWorth) return 'Net Worth';
    if (isPlanned) return 'Future Income';
    return isSavings ? 'Sinking Funds' : 'Investments';
  };

  // Focal number and subtitle
  const focalAmount = isPlanned ? totalExpectedIncome : isSavings ? totalSaved : totalInvested;
  const focalGoal = isSavings ? totalGoal : totalInvestmentGoal;
  const focalPct = focalGoal > 0 ? Math.round((focalAmount / focalGoal) * 100) : 0;

  const getFocalSubtitle = () => {
    if (isPlanned) return `expected across ${totalPaydays} payday${totalPaydays !== 1 ? 's' : ''}`;
    return `${focalPct}% there`;
  };

  // plan_09: Money plan - the mockup's top of Wealth.
  if (plan) {
    const card = { background: '#FFFFFF', borderRadius: 24, padding: 18, marginTop: 12 };
    const segBtn = (on) => ({
      flex: 1, border: 0, borderRadius: 999, padding: '8px 4px', fontSize: 13.5,
      fontWeight: on ? 600 : 500, color: on ? '#26252E' : '#6C6879',
      background: on ? '#FFFFFF' : 'transparent',
      boxShadow: on ? '0 1px 3px rgba(60,40,120,0.08)' : 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    });
    const C = {
      blue: { bg: '#DFE9F9', ink: '#3C6DB5' },
      mint: { bg: '#DFF2E7', ink: '#2F8756' },
      lilac: { bg: '#ECE6FA', ink: '#7A5DD3' },
      pink: { bg: '#FAE4ED', ink: '#C04B7C' },
    };
    const tile = (c, label, value) => (
      <div key={label} style={{ background: c.bg, borderRadius: 18, padding: '11px 12px', minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, color: '#55525F' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 16, fontWeight: 700, letterSpacing: '-0.4px', color: c.ink, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
      </div>
    );
    const heroC = isPlanned ? C.pink : isSavings ? C.blue : C.lilac;
    const heroTitle = isPlanned ? 'Future income' : isSavings ? 'Sinking funds' : 'Invested';
    const heroEmoji = isPlanned ? '\u{1F4EC}' : isSavings ? '\u{1F3AF}' : '\u{1F4C8}';
    const goalLine = focalGoal > 0 ? `${focalPct}% of your ${formatCurrencyWhole(focalGoal)} goal` : 'No goal set yet';
    const parseDay = (d) => new Date(String(d).length === 10 ? `${d}T12:00:00` : d);
    const nextDays = nextPaydayDate ? Math.round((parseDay(nextPaydayDate).setHours(0, 0, 0, 0) - new Date(today).setHours(0, 0, 0, 0)) / 86400000) : null;
    const nextName = nextPaydayName || 'Next payday';
    const nextText = nextDays === null ? null
      : nextDays < 0 ? `${nextName} is past due`
      : nextDays === 0 ? `${nextName} is today!`
      : nextDays === 1 ? `${nextName} is tomorrow!`
      : `${nextName} in ${nextDays} days!`;
    const locked = isNetWorth && !canViewHistory;
    const navBtn = (dir) => (
      <button
        onClick={() => (locked ? showFeaturePaywall('netWorthHistory') : dir < 0 ? onPrevMonth() : onNextMonth())}
        aria-label={dir < 0 ? 'Previous month' : 'Next month'}
        className="flex items-center justify-center"
        style={{ width: 30, height: 36, border: 0, background: 'none' }}
      >
        {locked ? <Lock className="w-3 h-3" style={{ color: '#ABA7B8' }} /> : dir < 0 ? <ChevronLeft className="w-4 h-4" style={{ color: '#8B879A' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#8B879A' }} />}
      </button>
    );
    return (
      <div className="px-4 pt-4 pb-2 wy-wealth-hero" data-testid="plan-wealth-hero">
        <div className="flex items-center justify-between" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="flex items-center" style={{ gap: 8 }}>{/* plan_26: How it works for whichever pill you're on */}
              <h1 style={{ margin: 0 }}>your wealth</h1>
              <button type="button" aria-label="How it works" onClick={() => onInfoClick && onInfoClick()} style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0, fontFamily: "'DM Sans', sans-serif" }}>i</button>{/* plan_27 */}
            </div>
          </div>
          {!isPlanned && (
            <div className="flex items-center flex-shrink-0 wy-month" style={{ height: 36, borderRadius: 999, background: '#FFFFFF', padding: '0 4px' }} data-patch="plan_10">
              {navBtn(-1)}
              <span style={{ fontSize: 14, fontWeight: 500, color: '#8B879A', minWidth: 28, textAlign: 'center' }}>{shortMonthNames[viewMonth.getMonth()]}</span>
              {navBtn(1)}
            </div>
          )}
        </div>

        <div className="flex" style={{ background: '#ECE8F4', borderRadius: 999, padding: 3, marginTop: 14 }}>
          <button type="button" onClick={() => onViewChange('networth')} style={segBtn(isNetWorth)}>Worth</button>
          <button type="button" onClick={() => onViewChange('savings')} style={segBtn(isSavings)}>Funds</button>
          <button type="button" onClick={() => onViewChange('investments')} style={segBtn(isInvestments)}>Invest</button>
          <button type="button" onClick={() => onViewChange('planned')} style={segBtn(isPlanned)}>
            {!canUseFutureIncome && <Lock className="w-3 h-3" />}Plan
          </button>
        </div>

        {banner}

        {!isNetWorth && (
          <div style={card} data-testid="plan-wealth-card">
            <div className="flex justify-between" style={{ gap: 14 }}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' }}>{heroTitle}</h2>
                <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-1px', color: '#26252E', marginTop: 4, lineHeight: 1.15 }}>{formatCurrencyWhole(focalAmount)}</div>
                {isPlanned ? (
                  <p style={{ margin: '3px 0 0', fontSize: 13.5, color: '#8B879A' }}>
                    {totalPaydays > 0 ? `coming in across ${totalPaydays} payday${totalPaydays !== 1 ? 's' : ''}` : 'nothing planned yet'}
                  </p>
                ) : (
                  <span style={{ display: 'inline-block', marginTop: 8, background: heroC.bg, color: heroC.ink, borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>{goalLine}</span>
                )}
              </div>
              {!isPlanned && path && chart ? <ViewToggle view={wView} onChange={setWView} /> : (
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 999, background: heroC.bg, fontSize: 20 }}>{heroEmoji}</span>
              )}
            </div>

            {!isPlanned && (path && (wView === 'path' || !chart) ? <div style={{ marginTop: 6 }}>{path}</div> : chart ? <div style={{ marginTop: 14 }}>{chart}</div> : null)}

            {isPlanned ? (
              <>
                {paySteps.length > 0 && (
                  <PlanPath
                    steps={paySteps}
                    progress={(() => {
                      const i = paySteps.findIndex((s) => s.now);
                      return paySteps.length > 1 && i > 0 ? i / (paySteps.length - 1) : 0;
                    })()}
                  />
                )}
                <div style={{ background: '#F4F1FB', borderRadius: 20, padding: '14px 16px', marginTop: 12 }} data-patch="plan_11">
                  <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A' }}>
                    {nextPaydayDate ? `Next up · ${parseDay(nextPaydayDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}` : 'Next up'}
                  </span>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#26252E', marginTop: 2 }}>
                    {nextText ? nextText + (nextPaydayAmount > 0 ? ` \u00b7 ${formatCurrencyWhole(nextPaydayAmount)}` : '') : 'Tap + to add a bonus, refund or extra pay'}
                  </span>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-3" style={{ gap: 8, marginTop: 14 }}>
                {tile(isSavings ? C.blue : C.lilac, 'Goal', formatCurrencyWhole(focalGoal || 0))}
                {tile(C.mint, 'Interest', `+${formatCurrencyWhole(isSavings ? savingsInterest : investmentInterest)}`)}
                {tile(isSavings ? C.lilac : C.blue, 'Projected', formatCurrencyWhole(isSavings ? totalProjected : totalInvestmentProjected))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-2 wy-wealth-hero wy-ph\" style={{ fontFamily: "'DM Sans', sans-serif", '--ph-bleed': '0px', '--ph-inset': '1rem' }}>
      {/* Title + month nav row - always shown (title covers Net Worth too now) */}
      <div className="flex items-center justify-between mb-3">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>{getHeroTitle()}</h1>
          <button onClick={onInfoClick} style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0 }}>i</button>
        </div>
        {!isPlanned && isNetWorth && (
          <div className="flex items-center gap-0.5 wy-month">
            <button onClick={() => canViewHistory ? onPrevMonth() : showFeaturePaywall('netWorthHistory')} className="w-4 h-4 flex items-center justify-center">
              {canViewHistory ? <ChevronLeft className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} /> : <Lock className="w-3 h-3 text-gray-400" />}
            </button>
            <span className="text-xs font-semibold px-1 min-w-[32px] text-center" style={{ color: '#a39fa0' }}>{shortMonthNames[viewMonth.getMonth()]}</span>
            <button onClick={() => canViewHistory ? onNextMonth() : showFeaturePaywall('netWorthHistory')} className="w-4 h-4 flex items-center justify-center">
              {canViewHistory ? <ChevronRight className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} /> : <Lock className="w-3 h-3 text-gray-400" />}
            </button>
          </div>
        )}
        {!isPlanned && !isNetWorth && (
          <div className="flex items-center gap-0.5 wy-month">
            <button onClick={onPrevMonth} className="w-4 h-4 flex items-center justify-center">
              <ChevronLeft className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} />
            </button>
            <span className="text-xs font-semibold px-1 min-w-[32px] text-center" style={{ color: '#a39fa0' }}>{shortMonthNames[viewMonth.getMonth()]}</span>
            <button onClick={onNextMonth} className="w-4 h-4 flex items-center justify-center">
              <ChevronRight className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} />
            </button>
          </div>
        )}
      </div>
      {/* Tab row — Worth / Funds / Invest / Plan, underline style matching Budget's tab bar */}
      <div className="flex items-center justify-center mb-3 wy-tabs" style={{ gap: '48px' }}>
        <button onClick={() => onViewChange('networth')} data-on={isNetWorth ? '1' : undefined} style={{ paddingBottom: '10px', fontSize: '14px', fontWeight: isNetWorth ? 700 : 500, color: isNetWorth ? '#1A1A1F' : '#a39fa0', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}>
          Worth
          {isNetWorth && (
            <span style={{ position: 'absolute', bottom: '-1px', left: '15%', right: '15%', height: '2px', background: accentColor, borderRadius: '2px' }} />
          )}
        </button>
        <button onClick={() => onViewChange('savings')} data-on={isSavings ? '1' : undefined} style={{ paddingBottom: '10px', fontSize: '14px', fontWeight: isSavings ? 700 : 500, color: isSavings ? '#1A1A1F' : '#a39fa0', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}>
          Funds
          {isSavings && (
            <span style={{ position: 'absolute', bottom: '-1px', left: '15%', right: '15%', height: '2px', background: accentColor, borderRadius: '2px' }} />
          )}
        </button>
        <button onClick={() => onViewChange('investments')} data-on={isInvestments ? '1' : undefined} style={{ paddingBottom: '10px', fontSize: '14px', fontWeight: isInvestments ? 700 : 500, color: isInvestments ? '#1A1A1F' : '#a39fa0', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}>
          Invest
          {isInvestments && (
            <span style={{ position: 'absolute', bottom: '-1px', left: '15%', right: '15%', height: '2px', background: accentColor, borderRadius: '2px' }} />
          )}
        </button>
        <button onClick={() => onViewChange('planned')} data-on={isPlanned ? '1' : undefined} style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingBottom: '10px', fontSize: '14px', fontWeight: isPlanned ? 700 : 500, color: isPlanned ? '#1A1A1F' : '#a39fa0', position: 'relative', background: 'none', border: 'none', cursor: 'pointer' }}>
          {!canUseFutureIncome && <Lock className="w-3 h-3" />}
          Plan
          {isPlanned && (
            <span style={{ position: 'absolute', bottom: '-1px', left: '15%', right: '15%', height: '2px', background: accentColor, borderRadius: '2px' }} />
          )}
        </button>
      </div>

      {/* Big focal number + subtitle — float directly on the page, no card */}
      {!isNetWorth && (
      <>
          <p className="hero-gradient-text" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '40px', fontWeight: 400, letterSpacing: '-1.5px', lineHeight: 1, marginBottom: '4px' }}>
            {formatCurrencyWhole(focalAmount)}
          </p>
          <p style={{ fontSize: '13px', color: '#9A9AA8', marginBottom: '14px' }}>
            {getFocalSubtitle()}
          </p>

        {/* Stat tiles */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {isPlanned ? (
            <>
              <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.pink.bg, padding: '12px 11px 10px' }}>
                <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="1" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>📋</div>
                <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.pink.text, marginBottom: '3px' }}>Paydays</span>
                <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.pink.text }}>{totalPaydays}</p>
              </div>
              <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.blue.bg, padding: '12px 11px 10px' }}>
                <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="2" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>📅</div>
                <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.blue.text, marginBottom: '3px' }}>Next</span>
                <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.blue.text }}>
                  {nextPaydayDate ? new Date(nextPaydayDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                </p>
              </div>
              <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.lavender.bg, padding: '12px 11px 10px' }}>
                <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="4" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>⏳</div>
                <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.lavender.text, marginBottom: '3px' }}>Days Away</span>
                <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.lavender.text }}>{getDaysUntilNextPayday()}</p>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.blue.bg, padding: '12px 11px 10px' }}>
                <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="2" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>🎯</div>
                <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.blue.text, marginBottom: '3px' }}>Goal</span>
                <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.blue.text }}>{formatCurrencyWhole(isSavings ? totalGoal : totalInvestmentGoal)}</p>
              </div>
              <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.green.bg, padding: '12px 11px 10px' }}>
                <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="3" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>✨</div>
                <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.green.text, marginBottom: '3px' }}>Interest</span>
                <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.green.text }}>+{formatCurrencyWhole(isSavings ? savingsInterest : investmentInterest)}</p>
              </div>
              <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.lavender.bg, padding: '12px 11px 10px' }}>
                <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="4" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>🔮</div>
                <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.lavender.text, marginBottom: '3px' }}>Projected</span>
                <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.lavender.text }}>{formatCurrencyWhole(isSavings ? totalProjected : totalInvestmentProjected)}</p>
              </div>
            </>
          )}
        </div>
      </>
      )}
    </div>
  );
};

export default WealthHero;
